// src/modules/Rules/components/modals/DeleteModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';

export interface DeleteModalRef {
  /**
   * Abre la modal de confirmación de eliminación.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   * @param name Nombre de la regla a mostrar en el mensaje.
   */
  open: (onConfirm: () => void, name?: string) => void;
}

export const DeleteModal = forwardRef<DeleteModalRef>((_, ref) => {
  const { t } = useTranslation('rulesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void>(() => {});
  const [ruleName, setRuleName] = useState<string>('');

  useImperativeHandle(ref, () => ({
    open: (onConfirm: () => void, name?: string) => {
      confirmRef.current = onConfirm;
      setRuleName(name || '');
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = () => {
    confirmRef.current();
    confirmRef.current = () => {};
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={t('modal.delete.title')} type="default" width="default">
      <div className="space-y-4">
        <p className="text-sm">{t('modal.delete.message', { name: ruleName })}</p>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('modal.buttons.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            {t('modal.buttons.delete')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

DeleteModal.displayName = 'DeleteModal';
